import { installEdgeFetch } from './lib/edge-fetch';
import { installChunkReload } from './lib/chunk-reload';
import { registerSW } from 'virtual:pwa-register';
import { installVersionWatch } from './lib/version-watch';
// Гонка мостов ДО всего остального: каждый /api-запрос приложения обязан уметь
// переехать на живой мост (ТСПУ душит края волнами по одному — 18 авг 2026).
installEdgeFetch();
// Провал ленивого чанка (оболочка старше истории ассетов) → однократная
// перезагрузка вместо мёртвого экрана (19 авг 2026, инцидент «биллинг не открылся»).
installChunkReload();
// Service Worker: вход панели без сети после первого визита (см. vite.config).
// immediate — новый деплой подхватывается сразу, юзер не залипает в старой сборке.
const updateSW = registerSW({ immediate: true });
// Сторож версии: оболочка в Telegram-WebView живёт сутками и сама не узнаёт о новой
// сборке — теперь узнаёт (build.json мимо кэшей + пинок SW) и обновляется при возврате
// человека в приложение, без убийства приложения и чистки кэша.
// onOutcome — исходы сторожа летят в метрики (mobile-gw): без этого stale-stuck
// (оболочка знает, что стара, но перезагрузка не помогла) был невидим — застрявшую
// на 13 суток оболочку нашла только жалоба ректора (1 сен 2026, TrackStudio 404).
installVersionWatch({
  updateServiceWorker: () => void updateSW(true),
  onOutcome: (outcome) => sendRum({ type: 'version_watch', outcome }),
});

import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import App from './ui/App';
import { queryClient } from './lib/query-client';
import { Toaster } from './components/ui/sonner';
import { initWebVitals } from './lib/observability';
import { bootDiag, installBootDiagnostics, trackAppBoot, sendRum } from './lib/rum';
import { getActiveEdge, servedBridge } from './lib/edge-fetch';
import { runNetprobeIfDue } from './lib/netprobe';
import './index.css';

// Локальные шрифты — Manrope (sans body), Cormorant Garamond (прямой
// editorial display, без курсива — решение ректора 2026-05-29),
// JetBrains Mono Variable (technical labels). Подгружаем через
// @fontsource чтобы не зависеть от Google Fonts CDN (Russia/VPN блокируют
// fonts.googleapis.com, и Studios превращались в serif fallback).
import '@fontsource/manrope/300.css';
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';
import '@fontsource/cormorant-garamond/300.css';
import '@fontsource/cormorant-garamond/400.css';
import '@fontsource/cormorant-garamond/400-italic.css';
import '@fontsource/cormorant-garamond/500.css';
import '@fontsource/cormorant-garamond/600.css';
import '@fontsource/cormorant-garamond/700.css';
import '@fontsource-variable/jetbrains-mono';
// Дайджест (Outfit) + Turn Theatre (Big Shoulders Display / Archivo / IBM Plex Mono) +
// Viewer (Space Grotesk / Instrument Serif) — те же правила: только локально. Внешние
// <link>/@import на fonts.googleapis.com блокировали рендер у юзеров без рабочего VPN
// (ТСПУ душит Google, инцидент 8 июл 2026) и дублировали уже локальные семейства.
import '@fontsource/outfit/300.css';
import '@fontsource/outfit/400.css';
import '@fontsource/outfit/500.css';
import '@fontsource/outfit/600.css';
import '@fontsource/outfit/700.css';
import '@fontsource/outfit/800.css';
import '@fontsource/outfit/900.css';
import '@fontsource/big-shoulders-display/300.css';
import '@fontsource/big-shoulders-display/400.css';
import '@fontsource/big-shoulders-display/600.css';
import '@fontsource/big-shoulders-display/800.css';
import '@fontsource/archivo/400.css';
import '@fontsource/archivo/500.css';
import '@fontsource/archivo/600.css';
import '@fontsource/archivo/800.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/ibm-plex-mono/600.css';
import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/instrument-serif/400.css';
import '@fontsource/instrument-serif/400-italic.css';

// ── Sentry init ──────────────────────────────────────────────────────────
// Only enabled in production builds with a DSN present. Dev sessions stay
// quiet so local errors don't pollute the project's issue stream.
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    // Modest default — 10% of transactions sampled. Bump per env via VITE_*
    // if/when we want more detail, but avoid swamping the free tier by default.
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    release: import.meta.env.VITE_GIT_SHA,
  });
}

// Web Vitals capture — после Sentry.init чтобы measurements уходили в active
// Sentry hub. Без DSN init'а Sentry.setMeasurement делает silent no-op,
// никаких ошибок не выбрасывает.
if (import.meta.env.PROD) {
  initWebVitals();
}

// Стейл-чанк после деплоя: при новом билде у lazy-чанков меняются хэши, старые файлы
// с сервера пропадают. Открытая старая вкладка при навигации пытается догрузить старый
// чанк → Vite кидает 'vite:preloadError' ("Failed to fetch dynamically imported module").
// Чиним прозрачно — один раз перезагружаем страницу на свежую версию (флаг-таймштамп в
// sessionStorage защищает от петли, если чанк реально недоступен).
window.addEventListener('vite:preloadError', () => {
  const KEY = 'padla-preload-reload-ts';
  const last = Number(sessionStorage.getItem(KEY) || '0');
  if (Date.now() - last < 10_000) return; // перезагружались <10с назад — не зацикливаемся
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root not found in index.html');
}
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <Toaster richColors position="bottom-right" />
    </QueryClientProvider>
  </React.StrictMode>
);

// RUM app_boot: шелл смонтирован, ждём ПЕРВЫЙ осевший запрос данных → шлём один
// раз с исходом (ловит и «шелл есть, но /api завис»). boot_started — из index.html
// ДО бандла. Всё в try/catch: телеметрия никогда не ломает панель (П5).
try {
  const bundleMs = Math.round(performance.now());
  bootDiag.t0 = bundleMs;
  let sent = false;
  const fire = (firstApiOk: boolean): void => {
    if (sent) return;
    sent = true;
    trackAppBoot({ bundleMs, firstApiMs: Math.round(performance.now()) - bundleMs, firstApiOk });
    unsub();
    clearTimeout(fallback);
    // Проба двух адресов панели (раз в сутки, lib/netprobe.ts): только когда панель
    // уже открылась, спустя паузу — не отнимает сеть у первых экранов человека.
    if (firstApiOk) setTimeout(() => void runNetprobeIfDue(), 5_000);
  };
  const unsub = queryClient.getQueryCache().subscribe((ev) => {
    // Донесение «маяка приложения нет» (rum.ts): сколько запросов данных осело — по событиям самого кэша.
    if (ev?.type === 'updated') {
      if (ev.action?.type === 'success') bootDiag.settledOk++;
      else if (ev.action?.type === 'error') bootDiag.settledErr++;
    }
    const s = ev?.query?.state?.status;
    if (s === 'success') fire(true);
    else if (s === 'error') fire(false);
  });
  // Фолбэк: если за 20с ни один запрос не осел (шелл поднялся, но всё висит) —
  // фиксируем как boot без успешного /api, чтобы «завис» был виден, не потерян.
  const fallback = setTimeout(() => fire(false), 20_000);
  // Страница уходит, а маяк приложения так и не ушёл — донесение с тем, что она видела (хвост «маяк приложения»).
  installBootDiagnostics({
    edge: () => ({
      own: getActiveEdge(window.location.origin) === window.location.origin,
      served: servedBridge(),
    }),
  });
} catch {
  /* RUM не критичен */
}
